import * as React from "react";
import { MinimalButton, Spinner, TextBox } from "@react-pdf-viewer/core";
import {
  Match,
  NextIcon,
  PreviousIcon,
  RenderSearchProps,
  SearchPlugin,
} from "@react-pdf-viewer/search";
import { useEffect } from "react";
import { TypeCommon } from "models/common";

enum SearchStatus {
  NotSearchedYet,
  Searching,
  FoundResults,
}

interface SearchSidebarProps {
  searchPluginInstance: SearchPlugin;
}

const SearchSidebar: React.FC<SearchSidebarProps> = ({
  searchPluginInstance,
}) => {
  const [searchStatus, setSearchStatus] = React.useState(
    SearchStatus.NotSearchedYet,
  );
  const [matches, setMatches] = React.useState<Match[]>([]);
  const { Search } = searchPluginInstance;

  const renderMatchSample = (match: Match): TypeCommon => {
    //  match.startIndex    match.endIndex
    //      |                       |
    //      ▼                       ▼
    //  ....[_____props.keyword_____]....

    const wordsBefore = match.pageText.substr(match.startIndex - 20, 20);
    let words = wordsBefore.split(" ");
    words.shift();
    const begin = words.length === 0 ? wordsBefore : words.join(" ");

    const wordsAfter = match.pageText.substr(match.endIndex, 60);
    words = wordsAfter.split(" ");
    words.pop();
    const end = words.length === 0 ? wordsAfter : words.join(" ");

    return (
      <div>
        {begin}
        <span style={{ backgroundColor: "rgb(255, 255, 0)" }}>
          {match.pageText.substring(match.startIndex, match.endIndex)}
        </span>
        {end}
      </div>
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey && event.key === "f") {
        event.preventDefault();

        const button = document.querySelector(
          ".rpv-core__minimal-button--selected",
        );

        if (button) {
          (button as TypeCommon).click();
          const inputSearch =
            document.getElementsByClassName("rpv-core__textbox");
          if (inputSearch) {
            setTimeout(() => {
              (inputSearch[0] as HTMLElement).focus();
            }, 100);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Search>
      {(renderSearchProps: RenderSearchProps): TypeCommon => {
        const {
          currentMatch,
          keyword,
          setKeyword,
          jumpToMatch,
          jumpToNextMatch,
          jumpToPreviousMatch,
          search,
        } = renderSearchProps;

        const handleSearchKeyDown = (e: React.KeyboardEvent): TypeCommon => {
          if (e.key === "Enter" && keyword && keyword.trim()) {
            setSearchStatus(SearchStatus.Searching);
            // tslint:disable-next-line:no-shadowed-variable
            search().then((matches: TypeCommon) => {
              setSearchStatus(SearchStatus.FoundResults);
              setMatches(matches);
            });
          }
        };

        // Auto search when keyword changes (with debounce)
        React.useEffect(() => {
          if (keyword && keyword.trim()) {
            const timeoutId = setTimeout(() => {
              setSearchStatus(SearchStatus.Searching);
              search().then((matches: TypeCommon) => {
                setSearchStatus(SearchStatus.FoundResults);
                setMatches(matches);
              });
            }, 500); // 500ms debounce

            return () => clearTimeout(timeoutId);
          }
          
          setSearchStatus(SearchStatus.NotSearchedYet);
          setMatches([]);
        }, [keyword, search]);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <div style={{ padding: ".5rem" }}>
              <div style={{ position: "relative" }}>
                <TextBox
                  placeholder={"検索"}
                  value={keyword}
                  onChange={setKeyword}
                  onKeyDown={handleSearchKeyDown}
                />
                {searchStatus === SearchStatus.Searching && (
                  <div
                    style={{
                      alignItems: "center",
                      display: "flex",
                      bottom: 0,
                      position: "absolute",
                      right: ".5rem",
                      top: 0,
                    }}
                  >
                    <Spinner size="1.5rem" />
                  </div>
                )}
              </div>
            </div>
            {searchStatus === SearchStatus.FoundResults && (
              <>
                {matches.length === 0 && (
                  <>
                    {" "}
                    <span style={{ marginLeft: "5px" }}>
                      該当データがありません。
                    </span>
                  </>
                )}
                {matches.length > 0 && (
                  <>
                    <div
                      style={{
                        alignItems: "center",
                        display: "flex",
                        padding: ".5rem",
                      }}
                    >
                      <div
                        style={{
                          color: "rgba(0, 0, 0, .5)",
                          fontSize: ".8rem",
                          marginRight: ".5rem",
                        }}
                      >
                        {matches.length} レコードが見つかりました。
                      </div>
                      <div style={{ marginLeft: "auto", marginRight: ".5rem" }}>
                        <MinimalButton onClick={jumpToPreviousMatch}>
                          <PreviousIcon />
                        </MinimalButton>
                      </div>
                      <MinimalButton onClick={jumpToNextMatch}>
                        <NextIcon />
                      </MinimalButton>
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid rgba(0, 0, 0, .2)",
                        flex: 1,
                        overflow: "auto",
                        padding: ".5rem 1rem",
                      }}
                    >
                      {matches.map((match, index) => (
                        <div key={index} style={{ margin: "1rem 0" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: ".5rem",
                            }}
                          >
                            <div>#{index + 1}</div>
                            <div
                              style={{
                                color: "rgba(0, 0, 0, .5)",
                                fontSize: ".8rem",
                                textAlign: "right",
                              }}
                            >
                              ページ {match.pageIndex + 1}
                            </div>
                          </div>
                          <div
                            style={{
                              backgroundColor:
                                currentMatch === index + 1
                                  ? "rgba(0, 0, 0, .1)"
                                  : "",
                              border: "1px solid rgba(0, 0, 0, .2)",
                              borderRadius: ".25rem",
                              cursor: "pointer",
                              overflowWrap: "break-word",
                              padding: ".5rem",
                            }}
                            onClick={(): TypeCommon => jumpToMatch(index + 1)}
                          >
                            {renderMatchSample(match)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      }}
    </Search>
  );
};

export default SearchSidebar;
